import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GetMembershipPlans } from "Services/API/Membership.Service";
import {
  CreateVisitType,
  DeleteVisitType,
  GetVisitTypes,
  GetVisitTypeVersions,
  PublishVisitType,
  ReorderVisitTypes,
  SetVisitTypeActive,
  UpdateVisitType,
  UploadVisitTypeIcon,
} from "Services/API/VisitTypes.service";

export const visitTypeKeys = {
  all: ["visitTypes"],
  versions: (id) => ["visitTypes", id, "versions"],
  memberships: ["visitTypes", "memberships"],
};

export const useVisitTypes = () =>
  useQuery(visitTypeKeys.all, GetVisitTypes);

export const useVisitType = (id) => {
  const query = useVisitTypes();
  return {
    ...query,
    data: query.data?.find((visitType) => visitType.id === id),
  };
};

export const useVisitTypeVersions = (id) =>
  useQuery(visitTypeKeys.versions(id), () => GetVisitTypeVersions(id), {
    enabled: !!id,
  });

export const useVisitTypeMemberships = () =>
  useQuery(visitTypeKeys.memberships, GetMembershipPlans, {
    select: (result) => {
      const memberships = Array.isArray(result) ? result : result?.data || [];
      return memberships.filter((membership) => membership?.sku);
    },
  });

const useInvalidatingMutation = (mutationFn, extraKeys = []) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: visitTypeKeys.all });
      extraKeys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
    },
  });
};

export const useCreateVisitType = () =>
  useInvalidatingMutation(CreateVisitType);

export const useUpdateVisitType = () =>
  useInvalidatingMutation(({ id, payload }) => UpdateVisitType(id, payload));

export const usePublishVisitType = (id) =>
  useInvalidatingMutation(() => PublishVisitType(id), [visitTypeKeys.versions(id)]);

export const useSaveAndPublishVisitType = (id) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      await UpdateVisitType(id, payload);
      return PublishVisitType(id);
    },
    onSuccess: (result, payload) => {
      // The editor badge reads from the visit-types list cache. Apply the
      // publish response immediately so it never waits on a list refetch.
      queryClient.setQueryData(visitTypeKeys.all, (current = []) =>
        current.map((visitType) =>
          visitType.id === id
            ? {
                ...visitType,
                ...payload,
                publishedVersion:
                  result?.version ?? visitType.publishedVersion,
                publishedVersionId:
                  result?.formVersionId ?? visitType.publishedVersionId,
              }
            : visitType
        )
      );

      // The optimistic patch above lacks server-computed fields (signed
      // iconUrl, resolved prices) — refetch in the background for the truth.
      queryClient.invalidateQueries({
        queryKey: visitTypeKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: visitTypeKeys.versions(id),
      });
    },
  });
};

export const useReorderVisitTypes = () =>
  useInvalidatingMutation(ReorderVisitTypes);

export const useSetVisitTypeActive = () =>
  useInvalidatingMutation(({ id, active }) => SetVisitTypeActive(id, active));

export const useDeleteVisitType = () =>
  useInvalidatingMutation(DeleteVisitType);

export const useUploadVisitTypeIcon = () =>
  useMutation({ mutationFn: UploadVisitTypeIcon });
